import { Button, CircularProgress, DialogContent, DialogTitle, MenuItem, TextField } from '@mui/material'
import Dialog from '@mui/material/Dialog'
import makeStyles from '@mui/styles/makeStyles'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DropZone } from '../common/DropZone'
import { ClientDocument } from '../../api/documentApi'
import { fileToBase64 } from '../../utils/fileToBase64'
import { useDocumentApi } from '../../hooks/useDocumentApi'
import { useSelector } from '../../redux/hooks/useSelector'

const useStyles = makeStyles(theme => ({
  dialogContainer: {
    width: '440px',
    maxWidth: '100%',
    margin: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  border: {
    border: `2px dashed ${theme.palette.grey[500]}`,
  },
  dropContainer: {
    height: '120px',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
  },
}))

interface Props {
  open: boolean
  clientId: number
  documents: ClientDocument[]
  onClose: () => void
  onUploaded: () => void
}

// Upload skenu podepsane smlouvy (vzor FileImportDialog) — POST /documents/scan,
// volitelne sparovani s vygenerovanym originalem (relatedDocumentId).
export const ScanUploadDialog: React.FC<Props> = ({ open, clientId, documents, onClose, onUploaded }) => {
  const classes = useStyles()
  const { t } = useTranslation()

  const [fileContent, setFileContent] = useState<string | null>(null)
  const [fileName, setFileName] = useState<string>('')
  const [relatedDocumentId, setRelatedDocumentId] = useState<number | ''>('')
  const { uploadScan } = useDocumentApi()
  const loaders = useSelector(state => state.loading.isLoading)
  const isUploading = !!loaders.find(loader => loader === 'uploadScan')

  // Sparovat lze jen s vygenerovanym originalem (ne s jinym skenem)
  const generatedDocuments = documents.filter(document => document.source === 'GENERATED')

  const onFileDrop = (acceptedFiles: File[]) => {
    const file = acceptedFiles[0]
    if (!file) return
    setFileName(file.name)
    fileToBase64(file).then(result => setFileContent(result))
  }

  const handleClose = () => {
    setFileContent(null)
    setFileName('')
    setRelatedDocumentId('')
    onClose()
  }

  const handleUpload = async () => {
    if (!fileContent || !fileName) return
    const uploaded = await uploadScan({
      clientId,
      fileName,
      fileContent,
      relatedDocumentId: relatedDocumentId === '' ? undefined : relatedDocumentId,
    })
    if (uploaded) {
      handleClose()
      onUploaded()
    }
  }

  return (
    <Dialog maxWidth="md" onClose={handleClose} open={open}>
      <DialogTitle>{t('UPLOAD_SCAN')}</DialogTitle>
      <DialogContent className={classes.dialogContainer}>
        <div className={classes.dropContainer}>
          {isUploading ? (
            <CircularProgress />
          ) : (
            <DropZone onFileDrop={onFileDrop} defaultText={fileName || t('FILE_DIALOG_MESSAGE')} className={classes.border} />
          )}
        </div>
        <TextField
          select
          variant="standard"
          label={t('RELATED_DOCUMENT')}
          value={relatedDocumentId}
          onChange={event => setRelatedDocumentId(event.target.value === '' ? '' : Number(event.target.value))}
        >
          <MenuItem value="">{t('WITHOUT_ORIGINAL')}</MenuItem>
          {generatedDocuments.map(document => (
            <MenuItem key={document.clientDocumentId} value={document.clientDocumentId}>
              {document.fileName} (v{document.version})
            </MenuItem>
          ))}
        </TextField>
        <div className={classes.buttonContainer}>
          <Button variant="outlined" color="primary" onClick={handleClose} disabled={isUploading}>
            {t('CANCEL')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleUpload}
            disabled={isUploading || !fileContent}
          >
            {t('CONFIRM')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
